import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseChart } from './BaseChart.js';
import { formatNumber, formatPercentage } from '../../utils/dataFormatters.js';
import { generateChartAriaLabel } from '../../utils/accessibility.js';
import type { FunnelChartProps } from '../../types/analytics.types.js';
import { funnelStageColor } from '../../utils/funnelStageColors.js';
import { adminChartBarStyle } from '../../../../shared/ui/adminChartSeries.js';

/**
 * Production-grade funnel chart with conversion percentages and drop indicators
 */
export const FunnelChart = memo<FunnelChartProps>(
  ({
    data,
    showConversion = true,
    showDropIndicators = true,
    isLoading,
    error,
    emptyMessage,
    emptyHint,
    title,
    subtitle,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
  }) => {
    const { t } = useTranslation('admin');
    const resolvedTitle = title ?? t('analytics.charts.funnelTitle');
    const resolvedSubtitle = subtitle ?? t('analytics.charts.funnelSubtitle');

    const funnelStepLabel = (stepKey: string): string =>
      t(`analytics.funnelSteps.${stepKey}`);

    const maxCount = useMemo(() => {
      return Math.max(...data.map((d) => d.count), 0);
    }, [data]);

    const chartAriaLabel = useMemo(
      () => ariaLabel || generateChartAriaLabel(resolvedTitle, data.length, resolvedSubtitle),
      [ariaLabel, resolvedTitle, data.length, resolvedSubtitle]
    );

    return (
      <BaseChart
        isLoading={isLoading}
        error={error}
        emptyMessage={emptyMessage}
        emptyHint={emptyHint}
        title={resolvedTitle}
        subtitle={resolvedSubtitle}
        aria-label={chartAriaLabel}
        aria-describedby={ariaDescribedBy}
      >
        {data.length > 0 ? (
          <div className="flex flex-col gap-4 max-md:gap-3" role="img" aria-label={chartAriaLabel}>
            <div className="flex flex-col gap-4 items-center">
              {data.map((step, index) => {
                const width = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                const prevCount = index > 0 ? data[index - 1]?.count ?? 0 : step.count;
                const drop = prevCount - step.count;
                const dropPercentage =
                  prevCount > 0 ? ((drop / prevCount) * 100).toFixed(1) : '0';

                return (
                  <div
                    key={step.step}
                    className="flex w-full cursor-pointer flex-col gap-2 rounded-lg p-3 transition-colors hover:bg-[var(--color-an-surface-hover)] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[var(--color-an-primary)] max-md:min-h-11"
                    role="group"
                    aria-label={
                      showConversion && index > 0
                        ? t('analytics.charts.funnelStepDropAria', {
                            step: funnelStepLabel(step.step),
                            count: formatNumber(step.count),
                            drop: dropPercentage,
                          })
                        : t('analytics.charts.funnelStepAria', {
                            step: funnelStepLabel(step.step),
                            count: formatNumber(step.count),
                          })
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-[var(--color-an-text)]">{funnelStepLabel(step.step)}</span>
                      <span className="text-base font-bold tabular-nums text-[var(--color-an-primary)] max-md:text-sm">{formatNumber(step.count)}</span>
                    </div>
                    <div className="relative h-3 w-full overflow-hidden rounded-full bg-[var(--color-an-surface-muted,var(--color-an-bg))] max-md:h-2.5">
                      <div
                        className="h-full rounded-full transition-[width] duration-300"
                        style={adminChartBarStyle(index, width, {
                          backgroundColor: funnelStageColor(index),
                          opacity: 1 - index * 0.07,
                        })}
                        aria-label={t('analytics.charts.funnelStepAria', {
                          step: funnelStepLabel(step.step),
                          count: formatNumber(step.count),
                        })}
                      />
                    </div>
                    {showConversion && index > 0 && prevCount > 0 ? (
                      <div className="text-right text-xs font-semibold text-[var(--color-an-success,#059669)]">
                        {t('analytics.charts.funnelConversion', {
                          percentage: formatPercentage((step.count / prevCount) * 100),
                        })}
                      </div>
                    ) : null}
                    {showDropIndicators && index > 0 && drop > 0 ? (
                      <div className="text-right text-xs font-semibold text-[var(--color-an-danger,#dc2626)]" aria-label={t('analytics.charts.funnelDropAria', { count: formatNumber(drop) })}>
                        ↓ {formatNumber(drop)} ({dropPercentage}%)
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </BaseChart>
    );
  }
);

FunnelChart.displayName = 'FunnelChart';

